import type { Volume } from '../types/content'

export const volumes: Volume[] = [
  {
    icon: '📘',
    number: 'Volume 1',
    title: 'Wrestling Foundations & Movement System',
    lessons: '25 Sessions',
    accent: '#0F2A52',
    subtitle: 'Build the foundation every wrestler needs.',
    problem:
      'Stance, motion, balance and reactions decide everything that comes later. This volume gives you a structured path through the fundamentals instead of isolated warm-up drills.',
    bullets: [
      'Athletic stance, motion and positioning',
      'Level changes and penetration movement',
      'Balance, body control and defensive movement',
      'Sprawls and reaction training',
      'Hand fighting foundations and movement integration',
    ],
    result:
      'Your athletes build the stance, balance, movement and reactions required for more advanced wrestling.',
    popular: true,
  },
  {
    icon: '📗',
    number: 'Volume 2',
    title: 'Takedowns, Entries & Neutral Wrestling',
    lessons: '25 Sessions',
    accent: '#D99B12',
    subtitle: 'Turn setups and entries into repeatable scoring opportunities.',
    problem:
      'Attacks fail when setups, entries and finishes are taught separately. This volume connects them into complete neutral wrestling progressions.',
    bullets: [
      'Hand-fighting setups and attack creation',
      'Single-leg attacks and finishes',
      'Double-leg attacks and power finishes',
      'High-crotch attacks, ankle picks and alternative attacks',
      'Takedown defense, re-attacks and neutral integration',
    ],
    result:
      'Athletes learn how to create openings, enter cleanly, finish attacks and continue wrestling when the first attempt is defended.',
  },
  {
    icon: '📙',
    number: 'Volume 3',
    title: 'Top Control, Breakdowns & Turning System',
    lessons: '25 Sessions',
    accent: '#A2710A',
    subtitle: 'Develop wrestlers who can control, pressure and score from the top.',
    problem:
      'Holding top position is not scoring. This volume builds pressure, breakdowns and turning systems that convert control into points.',
    bullets: [
      'Top-position fundamentals and pressure development',
      'Base destruction and breakdowns',
      'Spiral rides, wrist control, ankle rides and tight-waist systems',
      'Turns, tilts and near-fall scoring',
      'Pinning combinations and mat returns',
    ],
    result:
      'Athletes stop simply holding top position and begin using control to create scoring opportunities.',
  },
  {
    icon: '📕',
    number: 'Volume 4',
    title: 'Escapes, Reversals & Bottom Wrestling System',
    lessons: '25 Sessions',
    accent: '#C0392B',
    subtitle: 'Build confident wrestlers who can recover, escape and reverse.',
    problem:
      'Bottom position is where athletes panic. This volume teaches structure, hand control and a clear path back to neutral.',
    bullets: [
      'Bottom-position fundamentals and base recovery',
      'Hand control and wrist clearing',
      'Stand-ups and mat-return recovery',
      'Sit-outs, hip heists and switches',
      'Reversals, chain escapes and bottom-position decisions',
    ],
    result:
      'Athletes learn how to rebuild structure under pressure and create a clear path back to neutral or into a reversal.',
  },
  {
    icon: '📒',
    number: 'Volume 5',
    title: 'Chain Wrestling, Situational Drills & Match Intelligence',
    lessons: '25 Sessions',
    accent: '#6B4E9B',
    subtitle: 'Teach athletes what to do when the first plan changes.',
    problem:
      'Matches are decided in the moments between techniques. This volume trains chains, scrambles and decisions under score and clock pressure.',
    bullets: [
      'Attack-to-re-attack chains and neutral transitions',
      'Defense-to-offense and position-to-position wrestling',
      'Scramble decisions and reaction recognition',
      'Score awareness, clock awareness and protecting a lead',
      'Wrestling from behind, late-match situations and decision-making',
    ],
    result:
      "Athletes stop seeing techniques as isolated moves and begin connecting actions according to the opponent's reaction.",
  },
  {
    icon: '📓',
    number: 'Volume 6',
    title: 'Competition Development & Complete Practice System',
    lessons: '25 Sessions',
    accent: '#1E8E4E',
    subtitle: 'Bring the entire coaching system together.',
    problem:
      'Everyday practice and competition preparation need to belong to the same plan. This volume shows how to organize both across a full season.',
    bullets: [
      'Complete practice design and technical development sessions',
      'High-repetition drilling, situational and live wrestling development',
      'Competition-week planning and pre-competition sharpness',
      'Match strategy, tournament preparation and match simulations',
      'Wrestling-specific conditioning, recovery, assessment and season development',
    ],
    result:
      'You know how to organize training from everyday practices all the way through competition preparation, evaluation and long-term athlete development.',
  },
]

export const sessionAreas: { title: string; description: string }[] = [
  {
    title: 'Foundations & Movement',
    description:
      'Stance, motion, balance, level changes, hand fighting and defensive reactions.',
  },
  {
    title: 'Neutral Wrestling',
    description: 'Setups, entries, takedowns, finishes, defense and re-attacks.',
  },
  {
    title: 'Top Wrestling',
    description: 'Breakdowns, rides, pressure, turns, tilts and scoring.',
  },
  {
    title: 'Bottom Wrestling',
    description: 'Base recovery, stand-ups, sit-outs, switches, reversals and escapes.',
  },
  {
    title: 'Chain Wrestling',
    description: 'Re-attacks, transitions, scrambles and continuous wrestling.',
  },
  {
    title: 'Match Intelligence',
    description: 'Score awareness, clock management, tactical decisions and match scenarios.',
  },
  {
    title: 'Competition Preparation',
    description: 'Competition-week sessions, tournament preparation and match simulation.',
  },
  {
    title: 'Wrestling Conditioning',
    description: 'Wrestling-specific conditioning designed to preserve technique under fatigue.',
  },
  {
    title: 'Athlete Development',
    description: 'Assessment, progression, individual priorities and season planning.',
  },
]

export const coachingResources: string[] = [
  'Progression maps',
  'Decision trees',
  'Skill checkpoints',
  'Coaching cues',
  'Common mistakes and corrections',
  'Situational wrestling structures',
  'Match simulations',
  'Score-and-clock scenarios',
  'Practice builders',
  'Competition readiness tools',
  'Athlete development systems',
  'Assessment resources',
  'Season progression tools',
]
